import React, { useState, useCallback, useEffect, useRef } from 'react'
import styles from './TagEditor.module.css'
import { useTagsStore } from '../../store/tagsStore'
import TagsManagerModal from '../TagsManagerModal/TagsManagerModal'
import { Tag } from '../../types/types'
import { validateName } from '../../utils/security'
import SecureInput from '../../ui/SecureInput/SecureInput'

interface TagEditorProps {
    tags: Tag[]
    canEdit: boolean
    setValue: (field: string, value: any) => void
    maxTags?: number
    maxLength?: number
    label?: string
}

const TagEditor: React.FC<TagEditorProps> = ({
    tags,
    canEdit,
    setValue,
    maxTags = 100,
    maxLength = 30,
    label
}) => {
    const [input, setInput] = useState('')
    const [error, setError] = useState('')
    const [open, setOpen] = useState(false)
    const [listMaxHeight, setListMaxHeight] = useState(160)
    const [activeIdx, setActiveIdx] = useState(-1)
    const [suggestions, setSuggestions] = useState<Tag[]>([])
    const [showManager, setShowManager] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const globalTags = useTagsStore(state => state.tags)
    const addGlobalTag = useTagsStore(state => state.addTag)
    const lastRenamed = useTagsStore(state => state.lastRenamed)

    useEffect(() => {
        const query = input.trim().toLowerCase()
        const available = globalTags
        if (!query) {
            setSuggestions(available)
            return
        }
        const starts = available.filter(t => t.name.startsWith(query))
        const contains = available.filter(t => !t.name.startsWith(query) && t.name.includes(query))
        const merged = [...starts, ...contains].filter((v, i, arr) => arr.indexOf(v) === i)
        setSuggestions(merged)
    }, [globalTags, input])

    useEffect(() => {
        if (!lastRenamed) return
        const { oldName, newName } = lastRenamed
        if (tags.some(t => t.name === oldName)) {
            setValue('tags', tags.map(t => t.name === oldName ? { ...t, name: newName } : t))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastRenamed])

    // Remove form tags that were deleted from the global tag list
    useEffect(() => {
        const globalNames = new Set(globalTags.map(t => t.name))
        const filtered = tags.filter(t => globalNames.has(t.name))
        if (filtered.length !== tags.length) {
            setValue('tags', filtered)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalTags])

    const positionSuggestions = useCallback(() => {
        if (!wrapperRef.current) return
        const rect = wrapperRef.current.getBoundingClientRect()
        const viewportPadding = 12
        const spaceBelow = window.innerHeight - rect.bottom - viewportPadding
        const computedMaxHeight = Math.max(0, Math.min(260, Math.floor(spaceBelow)))

        setListMaxHeight(computedMaxHeight)
    }, [])

    useEffect(() => {
        if (!open) return
        positionSuggestions()
        const onReposition = () => positionSuggestions()
        window.addEventListener('resize', onReposition)
        window.addEventListener('scroll', onReposition, true)
        return () => {
            window.removeEventListener('resize', onReposition)
            window.removeEventListener('scroll', onReposition, true)
        }
    }, [open, positionSuggestions])

    const toggleTag = useCallback((tag: string) => {
        const normalized = tag.trim().toLowerCase()
        if (!normalized) return
        
        // Validate tag name
        const validation = validateName(normalized, 'Tag name')
        if (!validation.valid) {
            setError(validation.error || 'Invalid tag name')
            return
        }
        
        const sanitizedTag = validation.sanitized
        const exists = tags.some(t => t.name === sanitizedTag)
        if (exists) {
            setValue('tags', tags.filter(t => t.name !== sanitizedTag))
        } else {
            if (tags.length >= maxTags) {
                setError(`Maximum ${maxTags} tags allowed`)
                return
            }
            setValue('tags', [...tags, { id: null, name: sanitizedTag }])
            addGlobalTag({ name: sanitizedTag })
        }
        setInput('')
        setError('')
        setActiveIdx(-1)
    }, [tags, maxTags, setValue, addGlobalTag])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            if (!open) setOpen(true)
            setActiveIdx(i => Math.min((suggestions?.length ? i : -1) + 1, suggestions?.length - 1))
            return
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActiveIdx(i => Math.max(i - 1, 0))
            return
        }
        if (e.key === 'Escape') {
            if (open) {
                e.preventDefault()
                setOpen(false)
                setActiveIdx(-1)
            }
            return
        }
        if (e.key === 'Enter' || e.key === ',') {
            if (activeIdx >= 0 && suggestions[activeIdx]) {
                e.preventDefault()
                toggleTag(suggestions[activeIdx].name)
                return
            }
            if (input.trim()) {
                e.preventDefault()
                const trimmed = input.trim()
                if (trimmed.length > maxLength) {
                    setError(`Tag must be ${maxLength} characters or less`)
                    return
                }
                toggleTag(trimmed)
            }
        }
    }


    const handleRemoveTag = (removeIdx: number) => {
        setValue('tags', tags.filter((_: Tag, idx: number) => idx !== removeIdx))
    }

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            {label && <label className={styles.label}>{label}</label>}
          { !(!canEdit && tags.length === 0) && <div className={styles.tagsInputWrapper}>
                <div className={styles.tagsList}>
                    {tags.map((tag, idx) => (
                        <span key={tag.name} className={styles.tag} onClick={() => {setOpen(!open)}}>
                            {tag.name}
                            {canEdit && (
                                <button
                                    type="button"
                                    className={styles.removeBtn}
                                    onClick={() => handleRemoveTag(idx)}
                                    aria-label={`Remove tag ${tag}`}
                                >
                                    &times;
                                </button>
                            )}
                        </span>
                    ))}
                    {canEdit && (
                        <div
                            onBlur={() => setTimeout(() => setOpen(false), 150)}
                            onFocus={() => { setOpen(true) }}
                            onKeyDown={handleKeyDown}
                        >
                            <SecureInput
                                fieldType="text"
                                maxLength={20}
                                value={input}
                                onChange={(value) => {
                                    if (value.length <= maxLength) {
                                        setInput(value)
                                        setError('')
                                        setOpen(true)
                                        setActiveIdx(-1)
                                    } else {
                                        setError(`Tag must be ${maxLength} characters or less`)
                                    }
                                }}
                                placeholder={tags.length === 0 ? 'Add a tag and press Enter' : ''}
                                disabled={!canEdit || tags.length >= maxTags}
                                className={styles.inlineInput}
                                id="tag-editor-input"
                                name="tag-editor-input"
                                autoComplete="nope"
                                autoCorrect="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                list="autocompleteOff"
                            />
                        </div>
                    )}
                </div>
                {error && <div className={styles.error}>{error}</div>}
            </div>}
            {open && (
                <div
                    className={styles.suggestionsList}
                    role="listbox"
                    style={{ maxHeight: `${listMaxHeight}px` }}
                >
                    <div
                        className={styles.suggestionItem}
                        onMouseDown={e => { e.preventDefault(); setShowManager(true); setOpen(false) }}
                        style={{ fontWeight: 600, color: '#2563eb' }}
                    >
                        Manage tags…
                    </div>
                    {suggestions?.length > 0 && suggestions?.map((s, idx) => {
                        const matchLen = input.length
                        return (
                            <div
                                key={s.name}
                                role="option"
                                aria-selected={idx === activeIdx}
                                className={`${styles.suggestionItem} ${idx === activeIdx ? 'active' : ''}`}
                                onMouseDown={e => { e.preventDefault(); toggleTag(s.name) }}
                                onMouseEnter={() => setActiveIdx(idx)}
                            >
                                <span>
                                    {matchLen > 0 ? (
                                        <span>
                                            <span className={styles.suggestionMatch}>{s.name.slice(0, matchLen)}</span>{s.name.slice(matchLen)}
                                        </span>
                                    ) : s.name}
                                </span>
                                {tags.some(t => t.name === s.name) && (
                                    <span className={styles.suggestionIcon}>✓</span>
                                )}
                            </div>
                        )
                    })}
                    {suggestions?.length === 0 && !input && (
                        <div className={styles.emptySuggestions}>No tags yet – add some below or manage existing.</div>
                    )}
                </div>
            )}
            {open && suggestions?.length === 0 && input && (
                <div
                    className={styles.suggestionsList}
                    style={{ maxHeight: `${listMaxHeight}px` }}
                >
                    <div className={styles.emptySuggestions}>No matching tags</div>
                </div>
            )}
            {showManager && (
                <TagsManagerModal open={showManager} onClose={() => setShowManager(false)} assignedTags={tags} />
            )}
        </div>
    )
}

export default TagEditor
